<script lang="ts">
    import AttributesForm from "@apps/reviews/components/ReviewForm/AttributesForm.svelte";
    import FormManager from "@lib/FormManager/FormManager";
    import { ValidationStrategy } from "@lib/FormManager/types";
    import yupResolver from "@lib/FormManager/yupResolver";
    import { getTypedContext } from "@lib/svelte-typed-context";
    import { map } from "rxjs";
    import { onMount } from "svelte";
    import * as Yup from "yup";

    import ButtonWithIcon from "@components/Buttons/ButtonWithIcon.svelte";
    import Icon from "@components/Icon.svelte";
    import ArrowHeadIcon from "@components/icons/ArrowHeadIcon.svelte";
    import Scrollable from "@components/Scrollable.svelte";
    import { stepperControllerContextToken } from "@components/Stepper/context_tokens";
    import T from "@components/T.svelte";

    import { REVIEWS_FORM_STEPS_NAMES } from "../../../constants";
    import ReviewsWizardManager from "../../../managers/ReviewsWizardManager";
    import AttributesQuery from "../../../queries/AttributesQuery";

    export let reviewsWizardManager: ReviewsWizardManager;
    export let attributesQuery: AttributesQuery;

    const stepperController = getTypedContext(stepperControllerContextToken);
    const stepList$ = stepperController.stepList$;

    const attributes$ = attributesQuery.attributes$;
    const attributesFormSchema$ = attributesQuery.formSchema$;
    const attributesFormInitialValues$ = attributesQuery.formInitialValues$;

    let scrollPosition: number | undefined = undefined;
    let scrollElementContainer: HTMLDivElement;
    let scrollElement: HTMLDivElement;

    const isLastFormStep$ = stepList$.pipe(
        map(
            (arr) =>
                arr.length >= 2 &&
                arr[arr.length - 2].name === REVIEWS_FORM_STEPS_NAMES.ATTRIBUTES
        )
    );

    const formManager = reviewsWizardManager.cacheManager.get(
        REVIEWS_FORM_STEPS_NAMES.ATTRIBUTES,
        [$isLastFormStep$],
        () => {
            return new FormManager({
                initialValues: $attributesFormInitialValues$,
                resolver: yupResolver(
                    Yup.object({ ...$attributesFormSchema$ })
                ),
                validationStrategy: ValidationStrategy.OnSubmit,
                unregisterOnUnmount: false,
                onSubmit: ({ values, errors }) => {
                    if (!values || (errors && Object.values(errors).length > 0))
                        return;

                    reviewsWizardManager.collectFormData(values);
                    if ($isLastFormStep$) {
                        reviewsWizardManager.submitCollectedFormData();
                    } else {
                        stepperController.nextStep();
                    }
                },
            });
        }
    );

    $: {
        $isLastFormStep$;
        $attributesFormInitialValues$;
        $attributesFormSchema$;
        formManager.updateOptions((options) => {
            options.initialValues = $attributesFormInitialValues$;
            options.resolver = yupResolver(
                Yup.object({ ...$attributesFormSchema$ })
            );
            return options;
        });
    }

    const setNavButtons = () => {
        if ($isLastFormStep$) {
            reviewsWizardManager.setNextButton({
                type: "submit",
                isActive: true,
                onClick: () => {
                    formManager.submit();
                },
            });
        } else {
            reviewsWizardManager.setNextButton({
                type: "next",
                isActive: true,
                onClick: () => {
                    formManager.submit();
                },
            });
        }

        reviewsWizardManager.setPrevButton({
            type: "prev",
            isActive: true,
            onClick: () => {
                stepperController.prevStep();
            },
        });
    };

    $: {
        $isLastFormStep$;
        setNavButtons();
    }

    const handleScroll = (event: Event) => {
        if (!(event.currentTarget instanceof HTMLDivElement)) return;
        scrollPosition = event.currentTarget.scrollTop;
    };

    const handleGoToBottom = () => {
        if (!scrollElement) return;

        scrollElement.scrollTo({
            top: scrollElement.offsetHeight,
            behavior: "smooth",
        });
    };

    onMount(() => {
        scrollElement = scrollElementContainer.querySelector(
            ".gw-scrollable"
        ) as HTMLDivElement;
    });

    $: showScrollToBottomButton =
        scrollElement &&
        scrollElement.scrollHeight > scrollElement.offsetHeight &&
        (!scrollPosition || scrollPosition < 5);

    const errors$ = formManager.errors$;
</script>

<div class="gw-rv-form-attributes" bind:this={scrollElementContainer}>
    <Scrollable on:scroll={handleScroll} visible>
        <div class="gw-rv-form-attributes__scrollable-area">
            <h2 class="gw-rv-form-attributes__title">
                <T
                    namespace="reviews.ReviewsFormModalWidget"
                    translationKey="attributes_step_title"
                />
            </h2>

            <div class="gw-rv-form-attributes__content">
                <AttributesForm
                    attributes={$attributes$}
                    namespace={"reviews.ReviewsFormModalWidget"}
                    {formManager}
                    errors={$errors$}
                />
            </div>
        </div>
    </Scrollable>
    {#if showScrollToBottomButton}
        <div class="gw-rv-form-attributes__arrow-btn-wrap">
            <ButtonWithIcon
                on:click={handleGoToBottom}
                class="gw-rv-form-attributes__arrow-btn"
                ariaLabel="Scroll to bottom"
            >
                <Icon>
                    <ArrowHeadIcon />
                </Icon>
            </ButtonWithIcon>
        </div>
    {/if}
</div>

<style lang="postcss">
    :global(body) {
        overflow: hidden;
    }
    .gw-rv-form-attributes {
        width: 100%;
        height: 100%;
        position: relative;
        padding: 0 0.5em 0 0;

        &__scrollable-area {
            max-width: 570px;
            margin: 0 auto;
        }

        &__title {
            font-size: 1.8em;
            line-height: 1.2;
            font-weight: 600;
            margin: 2.5em 0 0;
        }

        &__content {
            margin: 1em 0;
        }

        &__arrow-btn-wrap {
            position: absolute;
            bottom: 10px;
            left: 50%;
            transform: translate(-50%, 0);
            z-index: 1000;
        }
    }

    :global(.gw-rv-form-attributes__arrow-btn) {
        box-shadow: 0px 2px 12px 4px rgba(20, 20, 20, 0.1);
        width: 2em;
        height: 2em;
        border-radius: 50%;
        background-color: #fff;
        transform: rotate(90deg);
    }
</style>
